/* eslint-disable @typescript-eslint/member-ordering */
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Subject, tap } from 'rxjs';

import { AnalyticsFacade } from '@amfam/shared/analytics';
import { AgentInfo, Driver, PolicyChangeRequestType, State } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService, LoadingSpinnerService, Option, ValidationService } from '@amfam/ui-kit';

import {
  ChangeRequest,
  ChangeRequestPolicies,
  DriverLicenseChange,
  RequestChange
} from '../../models';

@Component({
  selector: 'ds-change-request-driver-license',
  //standalone: true,
  //imports: [CommonModule],
  templateUrl: './change-request-driver-license.component.html',
  styleUrl: './change-request-driver-license.component.scss',
  standalone: false
})
export class ChangeRequestDriverLicenseComponent implements OnInit, OnDestroy {
  private stop$ = new Subject<void>();
  constructor(public spinner: LoadingSpinnerService) {}
  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
    this.spinner.stop();
  }
  @Input() driverLicense: Partial<RequestChange>;
  @Input() agents: AgentInfo[];
  @Input() formGroup: UntypedFormGroup;
  @Output() cancelEvent = new EventEmitter();
  copyService = inject(CopyService);
  modalService = inject(DsModalService);
  analyticsFacade = inject(AnalyticsFacade);
  agentsModal = 'agentsModal';
  states: State[] = this.copyService.getCopy('shared.usStates');
  driversLicenseConfig = this.copyService.getCopy('policy.driverLicenseChange');
  builder = inject(FormBuilder);
  driverOptions: Option[] = [];
  driverLicenseForm = this.builder.group({
    licenseNumber: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(25),
        ValidationService.driverLicenseNumberValidator
      ])
    ),
    state: new FormControl('', Validators.required),
    driver: new FormControl(null, Validators.required),

    reason: new FormControl('')
  });

  private _drivers: Driver[];
  @Input()
  set drivers(value: Driver[]) {
    this._drivers = value;
    this.setDriverOptions(value);
    this.driverLicenseForm.setControl(
      'driver',
      new FormControl<Option>(this.driverOptions[0], Validators.required)
    );
  }
  get drivers() {
    return this._drivers;
  }
  public get state(): AbstractControl<string> {
    return this.driverLicenseForm.get('state');
  }

  public get driversControl(): AbstractControl<Option> {
    return this.driverLicenseForm.get('driver');
  }

  public get licenseNumber(): AbstractControl<string> {
    return this.driverLicenseForm.get('licenseNumber');
  }

  public get reason(): AbstractControl {
    return this.driverLicenseForm.get('reason');
  }

  get valid(): boolean {
    return this.driverLicenseForm.valid;
  }

  @Output() changeRequestDriverLicense = new EventEmitter<ChangeRequestPolicies>();
  @Output() valueChangeEvent = new EventEmitter();

  setDriverOptions(drivers: Driver[]) {
    this.driverOptions = drivers.map(
      (d, i) =>
        <Option>{
          id: d.customerId,
          // text: `${d.name} - ${d.productDescription}`,
          text: d.name,
          value: i === 0 ? true : false
        }
    );
  }
  sendToAgent(agent: AgentInfo) {
    this.closeModal();
    const data = this.driverLicenseForm.getRawValue();

    const changeRequest = new ChangeRequest();
    changeRequest.policyChangeType = PolicyChangeRequestType.DRIVER_LICENSE_CHANGE;

    const requestChange: RequestChange = {
      agentId: agent.agentId,
      agentName: agent.name,
      agencyEmail: agent.agencyEmails?.at(0),
      agentEmail: agent.emails?.at(0),
      customerFirstName: this.driverLicense.customerFirstName,
      customerName: this.driverLicense.customerName,
      emailAddress: this.driverLicense.emailAddress,
      mailingAddress: this.driverLicense.mailingAddress,
      customerId: this.driverLicense.customerId,
      phoneNumbers: this.driverLicense.phoneNumbers,
      policyNumbers: this.driverLicense.policyNumbers,
      partnerId: this.driverLicense.partnerId
    };

    const driverLicenseChange: DriverLicenseChange = {
      agentId: agent.agentId,
      agentName: agent.name,
      customerId: this.driverLicense.customerId,
      partnerId: this.driverLicense.partnerId,
      customerFirstName: this.driverLicense.customerFirstName,
      customerName: this.driverLicense.customerName,
      emailAddress: this.driverLicense.emailAddress,
      mailingAdrress: this.driverLicense.mailingAddress,
      phoneNumbers: this.driverLicense.phoneNumbers,
      policyNumbers: this.driverLicense.policyNumbers,
      driver: data.driver.text,
      licenseNumber: data.licenseNumber,
      state: data.state,
      reason: data.reason || 'N/A'
    };
    changeRequest.requestChange = requestChange;
    changeRequest.driverLicenseChange = driverLicenseChange;
    this.changeRequestDriverLicense.emit(changeRequest);
  }

  public onSubmit() {
    if (this.agents.length > 1) {
      this.modalService.open(this.agentsModal);
    } else {
      this.spinner.start();
      this.sendToAgent(this.agents[0]);
    }
  }

  public cancel(): void {
    this.cancelEvent.emit();
  }

  closeModal() {
    this.modalService.close(this.agentsModal);
  }

  ngOnInit() {
    this.analyticsFacade.trackEvent({ event: 'update_drivers_license_start' });
    this.driverLicenseForm.valueChanges.pipe(tap(console.log)).subscribe();
  }
}
